/**
 * Spike Detection Service
 * Phase 2: Automated Signal Processing
 *
 * Detects interictal epileptiform discharges (IEDs):
 * - Spikes (20-70 ms duration)
 * - Sharp waves (70-200 ms duration)
 * - Polyspikes (multiple spikes in sequence)
 * - Spike-and-wave complexes
 *
 * Clinical Applications:
 * - Epilepsy diagnosis and localization
 * - Automated EEG screening
 * - Seizure burden quantification
 * - Medication efficacy monitoring
 *
 * Detection Algorithm:
 * 1. Amplitude thresholding (z-score based)
 * 2. Duration criteria (spike vs sharp wave)
 * 3. Morphology validation (sharp peak, slow return)
 * 4. Template matching for known patterns
 */

export interface SpikeDetectionParams {
  amplitudeThreshold: number; // Z-score threshold (default: 3.0)
  minDurationMs: number; // Minimum duration (default: 20ms)
  maxDurationMs: number; // Maximum duration (default: 200ms)
  samplingRate: number;
  refractoryPeriodMs: number; // Minimum time between spikes (default: 100ms)
}

export interface DetectedSpike {
  type: "spike" | "sharp_wave" | "polyspike";
  startIndex: number;
  peakIndex: number;
  endIndex: number;
  startTime: number; // seconds
  peakTime: number;
  endTime: number;
  amplitude: number;
  durationMs: number;
  zScore: number;
  polarity: "positive" | "negative";
  channelIndex: number;
  channelName?: string;
  confidence: number; // 0-1 confidence score
}

export interface SpikeDetectionResult {
  spikes: DetectedSpike[];
  spikeRate: number; // spikes per minute
  sharpWaveRate: number; // sharp waves per minute
  totalDetections: number;
  processingTime: number;
}

class SpikeDetectionService {
  /**
   * Detect spikes in a single channel
   */
  static detectSpikes(
    signal: number[],
    params: SpikeDetectionParams,
    channelIndex: number = 0,
    channelName?: string,
  ): SpikeDetectionResult {
    const startTime = performance.now();

    // Calculate signal statistics
    const mean = signal.reduce((sum, val) => sum + val, 0) / signal.length;
    const variance =
      signal.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
      signal.length;
    const stdDev = Math.sqrt(variance);

    // Convert duration to samples
    const minDurationSamples = Math.floor(
      (params.minDurationMs / 1000) * params.samplingRate,
    );
    const maxDurationSamples = Math.floor(
      (params.maxDurationMs / 1000) * params.samplingRate,
    );
    const refractorySamples = Math.floor(
      (params.refractoryPeriodMs / 1000) * params.samplingRate,
    );

    const spikes: DetectedSpike[] = [];
    let lastSpikeIndex = -refractorySamples;

    // Sliding window detection
    for (let i = 1; i < signal.length - 1; i++) {
      // Skip if in refractory period
      if (i - lastSpikeIndex < refractorySamples) {
        continue;
      }

      const value = signal[i];
      const zScore = (value - mean) / stdDev;

      // Check amplitude threshold
      if (Math.abs(zScore) < params.amplitudeThreshold) {
        continue;
      }

      // Detect peaks (local maxima/minima)
      const isPeak =
        (value > signal[i - 1] && value > signal[i + 1]) || // positive peak
        (value < signal[i - 1] && value < signal[i + 1]); // negative peak

      if (!isPeak) {
        continue;
      }

      // Find spike boundaries
      const boundaries = this.findSpikeBoundaries(
        signal,
        i,
        mean,
        minDurationSamples,
        maxDurationSamples,
      );

      if (!boundaries) {
        continue;
      }

      const { startIndex, endIndex } = boundaries;
      const durationMs = ((endIndex - startIndex) / params.samplingRate) * 1000;

      // Validate duration
      if (
        durationMs < params.minDurationMs ||
        durationMs > params.maxDurationMs
      ) {
        continue;
      }

      // Validate morphology
      const confidence = this.validateSpikeMorphology(
        signal,
        startIndex,
        i,
        endIndex,
        mean,
      );

      if (confidence < 0.5) {
        continue;
      }

      // Classify spike type
      const type = this.classifySpikeType(
        durationMs,
        signal,
        startIndex,
        endIndex,
      );

      const spike: DetectedSpike = {
        type,
        startIndex,
        peakIndex: i,
        endIndex,
        startTime: startIndex / params.samplingRate,
        peakTime: i / params.samplingRate,
        endTime: endIndex / params.samplingRate,
        amplitude: Math.abs(value - mean),
        durationMs,
        zScore: Math.abs(zScore),
        polarity: value > mean ? "positive" : "negative",
        channelIndex,
        channelName,
        confidence,
      };

      spikes.push(spike);
      lastSpikeIndex = endIndex;
    }

    // Calculate rates
    const recordingDurationMinutes = signal.length / params.samplingRate / 60;
    const spikeCount = spikes.filter(
      (s) => s.type === "spike" || s.type === "polyspike",
    ).length;
    const sharpWaveCount = spikes.filter((s) => s.type === "sharp_wave").length;

    const processingTime = performance.now() - startTime;

    return {
      spikes,
      spikeRate: spikeCount / recordingDurationMinutes,
      sharpWaveRate: sharpWaveCount / recordingDurationMinutes,
      totalDetections: spikes.length,
      processingTime,
    };
  }

  /**
   * Multi-channel spike detection
   */
  static detectMultiChannelSpikes(
    channels: number[][],
    params: SpikeDetectionParams,
    channelNames?: string[],
  ): SpikeDetectionResult[] {
    return channels.map((channel, index) =>
      this.detectSpikes(channel, params, index, channelNames?.[index]),
    );
  }

  /**
   * Find spike start and end boundaries
   */
  private static findSpikeBoundaries(
    signal: number[],
    peakIndex: number,
    baseline: number,
    minDuration: number,
    maxDuration: number,
  ): { startIndex: number; endIndex: number } | null {
    // Find start (backward from peak)
    let startIndex = peakIndex;
    const peakValue = signal[peakIndex];
    const isPositive = peakValue > baseline;

    // Go back until we cross baseline
    while (startIndex > 0 && startIndex > peakIndex - maxDuration) {
      const crossedBaseline = isPositive
        ? signal[startIndex] <= baseline
        : signal[startIndex] >= baseline;

      if (crossedBaseline) {
        break;
      }
      startIndex--;
    }

    // Find end (forward from peak)
    let endIndex = peakIndex;
    while (endIndex < signal.length - 1 && endIndex < peakIndex + maxDuration) {
      const crossedBaseline = isPositive
        ? signal[endIndex] <= baseline
        : signal[endIndex] >= baseline;

      if (crossedBaseline) {
        break;
      }
      endIndex++;
    }

    // Check if duration is valid
    const duration = endIndex - startIndex;
    if (duration < minDuration || duration > maxDuration) {
      return null;
    }

    return { startIndex, endIndex };
  }

  /**
   * Validate spike morphology
   * Returns confidence score 0-1
   */
  private static validateSpikeMorphology(
    signal: number[],
    startIndex: number,
    peakIndex: number,
    endIndex: number,
    baseline: number,
  ): number {
    let score = 0.0;

    // Criterion 1: Sharp peak (rapid rise)
    const riseTime = peakIndex - startIndex;
    const fallTime = endIndex - peakIndex;
    const asymmetry = riseTime / (riseTime + fallTime);

    // Spikes typically have sharper rise than fall (0.3-0.5)
    if (asymmetry >= 0.2 && asymmetry <= 0.6) {
      score += 0.3;
    }

    // Criterion 2: Amplitude significance
    const peakAmplitude = Math.abs(signal[peakIndex] - baseline);
    const avgAmplitude =
      signal
        .slice(startIndex, endIndex)
        .reduce((sum, v) => sum + Math.abs(v - baseline), 0) /
      (endIndex - startIndex);

    const prominence = peakAmplitude / avgAmplitude;
    if (prominence > 1.5) {
      score += 0.3;
    }

    // Criterion 3: Smooth waveform (no excessive noise)
    let smoothness = 0;
    for (let i = startIndex + 1; i < endIndex; i++) {
      const derivative = Math.abs(signal[i] - signal[i - 1]);
      smoothness += derivative;
    }
    smoothness /= endIndex - startIndex;

    if (smoothness < peakAmplitude * 0.5) {
      score += 0.2;
    }

    // Criterion 4: Returns to baseline
    const endProximity = Math.abs(signal[endIndex] - baseline);
    if (endProximity < peakAmplitude * 0.3) {
      score += 0.2;
    }

    return Math.min(score, 1.0);
  }

  /**
   * Classify spike type based on duration and pattern
   */
  private static classifySpikeType(
    durationMs: number,
    signal: number[],
    startIndex: number,
    endIndex: number,
  ): "spike" | "sharp_wave" | "polyspike" {
    // Check for polyspike (multiple peaks in window)
    const segment = signal.slice(startIndex, endIndex);
    const peaks = this.countPeaks(segment);

    if (peaks >= 2) {
      return "polyspike";
    }

    // Classify by duration
    if (durationMs < 70) {
      return "spike";
    } else {
      return "sharp_wave";
    }
  }

  /**
   * Count number of peaks in a segment
   */
  private static countPeaks(segment: number[]): number {
    let peakCount = 0;
    for (let i = 1; i < segment.length - 1; i++) {
      if (
        (segment[i] > segment[i - 1] && segment[i] > segment[i + 1]) ||
        (segment[i] < segment[i - 1] && segment[i] < segment[i + 1])
      ) {
        peakCount++;
      }
    }
    return peakCount;
  }

  /**
   * Get default detection parameters
   */
  static getDefaultParams(samplingRate: number): SpikeDetectionParams {
    return {
      amplitudeThreshold: 3.0,
      minDurationMs: 20,
      maxDurationMs: 200,
      samplingRate,
      refractoryPeriodMs: 100,
    };
  }

  /**
   * Get sensitive detection parameters (more detections, more false positives)
   */
  static getSensitiveParams(samplingRate: number): SpikeDetectionParams {
    return {
      amplitudeThreshold: 2.5,
      minDurationMs: 15,
      maxDurationMs: 250,
      samplingRate,
      refractoryPeriodMs: 50,
    };
  }

  /**
   * Get specific detection parameters (fewer false positives)
   */
  static getSpecificParams(samplingRate: number): SpikeDetectionParams {
    return {
      amplitudeThreshold: 4.0,
      minDurationMs: 25,
      maxDurationMs: 150,
      samplingRate,
      refractoryPeriodMs: 150,
    };
  }
}

export default SpikeDetectionService;
